import { CircleArrowIcon, SearchIconV2, StopIconSquare } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, GrowingTextarea } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useFocusedActions, useIsFocused } from '@va/util/hooks';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAiChatbotContext } from './aiChatbotContext';
import { AiModelsDropdown, NoModelsDropdownTooltip } from './AiModelsDropdown';
import { useChatDetailsContext } from './chatbot-tabs/chat-details/ChatDetailsContext';

type ChatInputProps = {
  onSubmit: (message: string) => void;
  focusOnMount?: boolean;
};

export const ChatInput = ({ onSubmit, focusOnMount }: ChatInputProps) => {
  const translate = useTranslate();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const { selectedModel, setSelectedModel, quota, mutateQuota } = useAiChatbotContext();
  const { isStreamingMessage, stopStreamingMessage } = useChatDetailsContext();

  const isContainerFocused = useIsFocused(inputContainerRef);

  const isQuotaLimitReached = useMemo(() => quota?.limitReached, [quota?.limitReached]);

  useEffect(() => {
    if (!focusOnMount) return;
    inputRef?.current?.focus();
  }, [focusOnMount]);

  const handleSubmit = useCallback(() => {
    if (isStreamingMessage) return;
    const message = value.trim();

    if (message) {
      onSubmit(message);
      setValue('');
    }

    inputRef?.current?.blur();
  }, [isStreamingMessage, onSubmit, value]);

  const handleOnClick = useCallback(() => {
    if (isStreamingMessage) {
      stopStreamingMessage();
      mutateQuota();
      return;
    }

    handleSubmit();
  }, [handleSubmit, isStreamingMessage, mutateQuota, stopStreamingMessage]);

  useFocusedActions({ ref: inputRef, onAction: handleSubmit, triggerKeys: ['Enter'] });

  return (
    <div className={classNames('flex gap-4u relative', { 'opacity-60': isQuotaLimitReached })}>
      <div className='min-h-24u flex-1 flex items-center rounded-6u overflow-hidden gap-1u'>
        <TooltipWrapper
          content={selectedModel ? null : <NoModelsDropdownTooltip />}
          interactive
          tooltipClassNames='!rounded-t-6u rounded-b-10u w-[15rem] shadow-outline'
          arrow={false}
          useDefaultStyle={false}
          disabled={!!selectedModel || isQuotaLimitReached}
          open={isContainerFocused || undefined}
        >
          <div
            ref={inputContainerRef}
            className={'pl-7u py-4u flex items-center bg-white flex-1 min-h-24u gap-4u'}
            tabIndex={selectedModel ? undefined : 0}
          >
            <SearchIconV2 className='w-10u h-10u text-gray-tundora shrink-0' />
            <GrowingTextarea
              ref={inputRef}
              containerClassName='w-full text-interface-regular leading-interface-regular'
              placeholder={translate('aiChatbot.chatInput.placeholder')}
              name='ai-assistant-message'
              id='ai-assistant-message'
              disabled={!selectedModel}
              onInput={(e) => setValue(e.currentTarget.value)}
              value={value}
            />
          </div>
        </TooltipWrapper>
        <AiModelsDropdown
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          buttonClassName='rounded-l-none'
          disabled={isQuotaLimitReached}
        />
      </div>
      <Button
        size='small'
        className='self-end'
        icon={(classes, color) =>
          isStreamingMessage ? (
            <StopIconSquare color={color} className={classes} />
          ) : (
            <CircleArrowIcon orientation='up' color={color} className={classes} />
          )
        }
        onClick={handleOnClick}
        disabled={!selectedModel}
        tooltip={!selectedModel ? translate('aiChatbot.chatInput.noModelSelected') : undefined}
        tooltipProps={{ disabled: isQuotaLimitReached }}
      />

      <TooltipWrapper content={translate('aiChatbot.limitWarning.tooltip')} disabled={!isQuotaLimitReached}>
        <div
          className={classNames('top-0 right-0 bottom-0 left-0', {
            hidden: quota && !isQuotaLimitReached,
            'cursor-not-allowed absolute': isQuotaLimitReached,
          })}
        />
      </TooltipWrapper>
    </div>
  );
};
